import { useLocation } from '@tanstack/react-router';
import { Search, SlidersHorizontal } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDebounce } from '@/hooks/use-debounce';
import { Field, FieldSelect, SearchField } from './admin/field';
import { FilterSheet } from './admin/filter-sheet';
import { AsyncSelect } from './async-select';
import { Badge } from './badge';
import { Button } from './button';
import { Icon } from './icon';

export interface Filter<T> {
  type: 'text' | 'number' | 'select' | 'date' | 'period';
  label: string;
  name: keyof T;
  defaultValue?: any;
  selectOptions?: {
    useQuery: any;
    getOptionValue: (o: any) => any;
    getDisplayValue: (o: any) => any;
  };
  options?: { label: string; value: string }[];
}

interface Props<T> {
  filters: Filter<T>[];
  onSearch: (data: T) => void;
  /** Filtros já aplicados ao abrir a tela (só quando não há busca salva na sessão pra rota). */
  defaultValues?: Record<string, any>;
  /** Rótulo exibido no chip de filtros assíncronos (select com `selectOptions`) já aplicados por padrão. */
  defaultLabels?: Record<string, string>;
}

const formatDateValue = (value: string) => {
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

export const SearchToolbar = <T,>({
  filters,
  onSearch,
  defaultValues,
  defaultLabels,
}: Props<T>) => {
  const { pathname } = useLocation();

  const storageKey = 'search';

  const [open, setOpen] = useState(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [displayLabels, setDisplayLabels] = useState<Record<string, string>>(
    defaultLabels ?? {},
  );

  const form = useForm<Record<string, any>>({
    defaultValues: {
      search: '',
    },
  });

  useEffect(() => {
    const raw = sessionStorage.getItem(storageKey);

    if (raw) {
      const saved = JSON.parse(raw) as {
        pathname: string;
        values: Record<string, any>;
      };

      if (saved.pathname === pathname) {
        form.reset(saved.values);
        onSearch?.(saved.values as T);

        const keys = Object.keys(saved.values).filter((key) => key !== 'search');
        if (keys.length) {
          setIsAdvancedSearch(true);
        }
        return;
      }

      sessionStorage.removeItem(storageKey);
    }

    if (defaultValues && Object.keys(defaultValues).length) {
      const initial = { search: '', ...defaultValues };
      form.reset(initial);
      setIsAdvancedSearch(true);
      onSearch?.(initial as T);
    }
  }, [pathname]);

  useEffect(() => {
    const sub = form.watch((values) => {
      sessionStorage.setItem(storageKey, JSON.stringify({ pathname, values }));
    });
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    return () => {
      const raw = sessionStorage.getItem(storageKey);

      if (!raw) return;

      const saved = JSON.parse(raw) as {
        pathname: string;
        values: string;
      };

      if (saved.pathname !== pathname) {
        sessionStorage.removeItem(storageKey);
      }
    };
  }, [pathname]);

  const values = form.watch();

  const debouncedSearch = useDebounce(values.search, 500);

  useEffect(() => {
    if (hasAdvancedFilters) return;

    onSearch?.(form.getValues() as T);
  }, [debouncedSearch]);

  const isTyping = !!values.search && values.search !== debouncedSearch;

  const hasAdvancedFilters = useMemo(() => {
    return filters.some((f) => {
      if (f.type === 'period') {
        const from = values[`${f.name as string}From`];
        const to = values[`${f.name as string}To`];
        return (from && from !== '') || (to && to !== '');
      }

      const v = values[f.name as string];
      return v && v !== '';
    });
  }, [values]);

  useEffect(() => {
    if (hasAdvancedFilters && values.search) {
      form.setValue('search', '');
    }
  }, [hasAdvancedFilters]);

  const activeFilters = useMemo(() => {
    return filters
      .map((f) => {
        if (f.type === 'period') {
          const from = values[`${f.name as string}From`];
          const to = values[`${f.name as string}To`];

          if (!from && !to) return null;

          const display = `${from ? formatDateValue(from) : '...'} até ${to ? formatDateValue(to) : '...'}`;

          return {
            name: f.name as string,
            label: f.label,
            value: { from, to },
            display,
            period: true,
          };
        }

        const value = values[f.name as string];
        if (!value || value === 'all') return null;

        let display = value;

        if (f.type === 'date') {
          display = formatDateValue(value);
        }

        if (f.type === 'select') {
          if (f.options) {
            const option = f.options?.find((o) => o.value === value);
            if (option) display = option.label;
          }

          if (f.selectOptions) {
            const label = displayLabels[f.name as string];
            if (label) display = label;
          }
        }

        return {
          name: f.name as string,
          label: f.label,
          value,
          display,
        };
      })
      .filter(Boolean);
  }, [values]);

  const handleSubmit = (data: any) => {
    onSearch?.(data);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch?.(form.getValues() as any);
      setOpen(false);
    }
  };

  const handleDrawerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsAdvancedSearch(true);
      onSearch?.(form.getValues() as any);
      setOpen(false);
    }
  };

  const searchFields = filters
    .filter((f) => f.type === 'text' && f.name !== 'id')
    .map((f) => f.label.toLowerCase())
    .join(', ');

  return (
    <div className="space-y-4 mb-6 relative w-full">
      <AnimatePresence>
        <div key="search-bar" className="flex items-center gap-2.5">
          <SearchField
            {...form.register('search')}
            icon={<Search className="size-[17px]" />}
            placeholder={`Buscar por ${searchFields}`}
            disabled={hasAdvancedFilters}
            onKeyDown={handleKeyDown}
          />

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex h-14 shrink-0 items-center gap-2 rounded-[14px] border border-border px-5 text-[13.5px] font-semibold text-muted-foreground transition-colors hover:border-ring hover:bg-raised"
          >
            <SlidersHorizontal className="size-[16px]" /> Filtros
          </button>

          <FilterSheet
            open={open}
            onOpenChange={setOpen}
            onClear={() => {
              setIsAdvancedSearch(false);
              form.reset({ search: '' });
              handleSubmit({});
              setOpen(false);
            }}
            onApply={form.handleSubmit((data) => {
              setIsAdvancedSearch(true);
              handleSubmit(data);
              setOpen(false);
            })}
          >
            {filters.map((f) => {
              if (f.type === 'period') {
                const fromName = `${f.name as string}From`;
                const toName = `${f.name as string}To`;

                return (
                  <div key={f.name as string} className="contents">
                    <Controller
                      name={fromName}
                      control={form.control}
                      render={({ field }) => (
                        <Field
                          {...field}
                          label={`${f.label} (de)`}
                          type="date"
                          onKeyDown={handleDrawerKeyDown}
                        />
                      )}
                    />
                    <Controller
                      name={toName}
                      control={form.control}
                      render={({ field }) => (
                        <Field
                          {...field}
                          label={`${f.label} (até)`}
                          type="date"
                          onKeyDown={handleDrawerKeyDown}
                        />
                      )}
                    />
                  </div>
                );
              }

              return (
                <Controller
                  key={f.name as string}
                  name={f.name as string}
                  control={form.control}
                  render={({ field }) => (
                    <>
                      {['text', 'number', 'date'].includes(f.type) && (
                        <Field
                          {...field}
                          label={f.label}
                          type={f.type}
                          onKeyDown={handleDrawerKeyDown}
                        />
                      )}

                      {f.type === 'select' &&
                        (f.options ? (
                          <FieldSelect
                            label={f.label}
                            value={field.value ?? f?.defaultValue ?? ''}
                            onChange={field.onChange}
                            options={f.options}
                          />
                        ) : (
                          <AsyncSelect
                            all
                            clearable
                            label={f.label}
                            useQuery={f.selectOptions?.useQuery}
                            getOptionValue={f.selectOptions?.getOptionValue as any}
                            getDisplayValue={f.selectOptions?.getDisplayValue as any}
                            value={field.value ?? f.defaultValue}
                            onChange={(value, option) => {
                              setDisplayLabels((prev) => ({
                                ...prev,
                                [f.name as string]:
                                  !value || value === 'all'
                                    ? undefined
                                    : f?.selectOptions?.getDisplayValue(option),
                              }));

                              field.onChange(value);
                            }}
                          />
                        ))}
                    </>
                  )}
                />
              );
            })}
          </FilterSheet>
        </div>

        {isAdvancedSearch && activeFilters.length > 0 ? (
          <motion.div
            key="active-filters"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-wrap gap-2"
          >
            {activeFilters.map((f: any) => (
              <motion.div
                key={f.name}
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
              >
                <Badge className="flex items-center gap-2 px-3 py-1">
                  {f.label}: {f.display}
                  <button
                    type="button"
                    onClick={() => {
                      if (f.period) {
                        form.setValue(`${f.name}From`, '');
                        form.setValue(`${f.name}To`, '');
                        const values = form.getValues() as any;
                        values[`${f.name}From`] = '';
                        values[`${f.name}To`] = '';
                        onSearch?.(values);
                        return;
                      }

                      form.setValue(f.name, '');
                      const values = form.getValues() as any;
                      values[f.name] = '';
                      onSearch?.(values);
                    }}
                    className="opacity-70 hover:opacity-100"
                  >
                    <Icon name="x" size={14} />
                  </button>
                </Badge>
              </motion.div>
            ))}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAdvancedSearch(false);
                form.reset({ search: '' });
                handleSubmit({});
              }}
            >
              Limpar tudo
            </Button>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
              duration: 0.2,
            }}
            className="flex items-center justify-center gap-1"
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: [1, 1.6, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.15,
                }}
                className="size-1.5 rounded-full bg-muted-foreground"
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
